import { ICartItem, IRentCartItem } from '@/services/cart/models/cart';
import { Box, Typography } from '@mui/material';
import CartItem from './CartItem';
import { getCartTypeFromLop } from '@/utils/cart';
import { useCart } from '@/hooks/useCart';
import { useNotification } from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/NotifyToast';
import FlexBox from '@/atomic-components/FlexBox';
import { getStyle } from '@/utils/theme/fonts';
import { getColor } from '@/utils/theme/colors';
import ActionsForCartItemInStock from './CartItem/ActionsForCartItemInStock';
import { ApiState } from '@/models/generics';
import { useEffect, useState } from 'react';
import { useFullScreenLoader } from '@/hooks/useFullScreenLoader';
import TenureSelectorDrawer from '@/components/TenureSelectorDrawer';
import LencoButton from '@/atomic-components/LencoButton';
import { useRouter } from 'next/router';
import { PdpUrlHelper } from '@/utils/seo-url-helper';
import { LencoTypography } from '@/atomic-components/LencoTypography';
import { dataLayerPushWithEvent, emitMoeEvent } from '@/utils/dataLayer';
import TenureSelector from './TenureSelector';
import { cartService } from '@/services/cart';
import { useDispatch } from 'react-redux';

const OutOfStockLabel = ({ message }: { message: string }) => (
	<Box
		sx={{
			...getStyle('Tiny_Medium'),
			color: getColor('Gray -1'),
			backgroundColor: getColor('Danger -4'),
			padding: '8px 16px',
			minWidth: 'max-content',
			borderRadius: '16px',
		}}
	>
		{' '}
		{message || 'OUT OF STOCK'}
	</Box>
);

type TCartItemListProps = {
	items: ICartItem[];
	onDeleteClicked: (item: ICartItem) => void;
	viewSimilarClicked: () => void;
	screen?: string;
	vertical?: any;
};

const CartItemList = (props: TCartItemListProps) => {
	const { cartActions } = useCart();

	const {
		actions: { notifyUser },
	} = useNotification();
	const { loader } = useFullScreenLoader();
	const router = useRouter();

	const [updateQuantityViewState, setUpdateQuantityViewState] =
		useState<ApiState>(ApiState.IDLE);
	const [itemSelectedForTenureSelector, setItemSelectedForTenureSelector] =
		useState<ICartItem>();
	const [updateTenureViewState, setUpdateTenureViewState] = useState<ApiState>(
		ApiState.IDLE
	);

	const [bulkActionDrawerOpen, setBulkActionDrawerOpen] = useState(false);
	const [bulkActionDrawerData, setBulkActionDrawerData] = useState<any>();

	const tenureSelectorVisible = !!itemSelectedForTenureSelector;

	const { rentCart, cart } = useCart();

	function updateCartItemQuantity(item: ICartItem, targetQuantity: number) {
		const cartType = getCartTypeFromLop(item?.lineOfProduct);

		setUpdateQuantityViewState(ApiState.LOADING);
		loader.show();

		return cartActions
			.updateCartItemQuantity(item.id, cartType, {
				tenure: item.tenure,
				quantity: targetQuantity,
			})
			.then((res) => {
				notifyUser({
					message: res.data?.data?.message,
					severity: NotificationSeverity.SUCCESS,
				});
			})
			.finally(() => {
				setUpdateQuantityViewState(ApiState.COMPLETED);
				loader.hide();
			});
	}

	function openTenureSelector(cartItem?: ICartItem) {
		emitMoeEvent('Cart Tenure Selector Opened', {
			product_id: cartItem?.product?.id,
		});

		setItemSelectedForTenureSelector(cartItem);
	}

	function closeTenureSelector() {
		setItemSelectedForTenureSelector(undefined);
	}
	function showBulkTenureSelector() {
		dataLayerPushWithEvent('moe_event', {
			moe_event_name: 'Cart Explore Payment Plans',
			moe_event_data: {
				screen_name: 'Cart Page',
				selected_tenure: rentCart?.tenureSelector?.selectedTenure,
				position: 'cart',
			},
		});

		setBulkActionDrawerData(rentCart?.tenureSelector);
		setBulkActionDrawerOpen(true);
	}
	function bulkActionDrawerClose() {
		setBulkActionDrawerOpen(false);
		setBulkActionDrawerData(undefined);
	}

	function bulkTenureUpdate(tenure: number, price?: number) {
		loader.show();
		setUpdateTenureViewState(ApiState.LOADING);

		if (price) {
			emitMoeEvent('Cart Bulk Tenure Switched', {
				price: price,
				old_bulk_tenure_selected: rentCart?.tenureSelector?.selectedTenure,
				new_bulk_tenure_selected: tenure,
			});
		}

		cartService
			.bulkTenureUpdate(cart?.id as number, { tenure: tenure })
			.then((res) => {
				if (res?.data?.success) {
					notifyUser({
						severity: NotificationSeverity.SUCCESS,
						message: 'Tenure updated successfully',
					});
					cartActions.getCart();
				}
			})
			.catch((err) => {
				notifyUser({
					severity: NotificationSeverity.ERROR,
					message: 'Failed to update tenure',
				});
				console.log(err);
			})
			.finally(() => {
				loader.hide();
				setUpdateTenureViewState(ApiState.COMPLETED);
				setBulkActionDrawerOpen(false);
			});
	}

	function onTenureChange(selectedTenure: number) {
		setUpdateTenureViewState(ApiState.LOADING);
		loader.show();

		cartActions
			.updateTenure(Number(itemSelectedForTenureSelector?.id), {
				tenure: selectedTenure,
				quantity: itemSelectedForTenureSelector?.quantity as number,
			})
			.then((res) => {
				if (res.data.success) {
					notifyUser({
						message: 'Tenure updated successfully!',
						severity: NotificationSeverity.SUCCESS,
					});
				}
			})
			.catch((err) => {
				notifyUser({
					message: err?.message,
					severity: NotificationSeverity.ERROR,
				});
			})
			.finally(() => {
				setUpdateTenureViewState(ApiState.COMPLETED);
				closeTenureSelector();
				loader.hide();
			});
	}

	function navigateToPdp(lop: string, permalink: string) {
		router.push(PdpUrlHelper({ lop, permalink }));
	}
	const tenureSelectvisible =
		rentCart?.tenureSelector?.upfrontPaymentPlans?.paymentPlans;

	return (
		<Box>
			<TenureSelectorDrawer
				productId={itemSelectedForTenureSelector?.product?.id}
				open={tenureSelectorVisible}
				onClose={closeTenureSelector}
				currentTenure={itemSelectedForTenureSelector?.tenure}
				quantity={itemSelectedForTenureSelector?.quantity}
				onTenureChange={onTenureChange}
				isUpdatingTenure={updateTenureViewState == ApiState.LOADING}
				upfrontOffers={
					(itemSelectedForTenureSelector as IRentCartItem)?.upfrontOffer
				}
				screen='Cart'
			/>
			<TenureSelectorDrawer
				open={bulkActionDrawerOpen}
				onClose={bulkActionDrawerClose}
				onTenureChange={bulkTenureUpdate}
				currentTenure={rentCart?.tenureSelector?.selectedTenure}
				isUpdatingTenure={updateTenureViewState == ApiState.LOADING}
				upfrontOffers={{
					...rentCart?.tenureSelector,
					tenureSelected: {
						monthlyPaymentPlans: rentCart?.tenureSelector?.monthlyPaymentPlans,
						upfrontPaymentPlan: rentCart?.tenureSelector?.upfrontPaymentPlans,
					},
				}}
				screen='Cart'
				tenurePosition='bulk'
			/>
			{props?.screen == 'CART' &&
				rentCart?.rentalCartItems?.length &&
				rentCart?.tenureSelector && (
					<TenureSelector
						tenureSelectorvisible={tenureSelectvisible}
						setItemSelectedForTenureSelector={bulkActionDrawerOpen}
						bulkTenureUpdate={showBulkTenureSelector}
						tenureSelector={rentCart?.tenureSelector}
						closeTenureSelector={closeTenureSelector}
						openTenureSelector={showBulkTenureSelector}
					/>
				)}
			{props.items?.map((cartItem, index) => {
				const isLastItem = props.items.length - 1 !== index;
				let tenureData: any = {
					monthly: [],
					upfront: [],
				};

				let tenureMap: any = {};
				if (cartItem?.upfrontOffer && cartItem?.upfrontOffer?.tenureSelector) {
					Object.values(cartItem?.upfrontOffer?.tenureSelector)?.map(
						(d: any) => {
							d?.paymentPlans?.map((plans: any) => {
								if (plans?.tenure == 1) {
									tenureData.monthly.push({
										tenure: plans.tenure,
										displayValue: plans.label,
									});
								} else {
									tenureData.upfront.push({
										tenure: plans.tenure,
										displayValue: plans.label,
									});
								}
								tenureMap = {
									...tenureMap,
									[plans?.tenure]: plans?.selectorLabel,
								};
							});
						}
					);
					// setTenureLabels(tenureMap)
					// setTenures(tenureData)
				}

				return (
					<Box
						sx={{
							...(isLastItem && {
								borderBottom: '1px solid #E8E8E8',
							}),
						}}
						key={`${cartItem.id}-${cartItem.product.name}`}
					>
						<CartItem
							item={cartItem}
							onDeleteClicked={props.onDeleteClicked}
							onUpdateItemQuantity={updateCartItemQuantity}
							onItemClicked={() =>
								navigateToPdp(
									cartItem.lineOfProduct,
									cartItem.product.permalink
								)
							}
							Actions={
								<Box>
									<Box mt='16px' width='100%'>
										{!cartItem.isOutOfStock ? (
											<ActionsForCartItemInStock
												vertical={props.vertical}
												item={cartItem}
												isUpdatingQuantity={
													updateQuantityViewState == ApiState.LOADING
												}
												openTenureSelector={() => openTenureSelector(cartItem)}
												updateCartItemQuantity={(quantity: number) =>
													updateCartItemQuantity(cartItem, quantity)
												}
												labels={tenureMap}
											/>
										) : (
											<LencoButton
												variant='contained'
												color='secondary'
												sx={{
													minWidth: '140px',
													mt: '8px',
													textTransform: 'none',
												}}
												onClick={(e) => {
													e.stopPropagation();
													props.viewSimilarClicked();
												}}
											>
												<LencoTypography sx={getStyle('Small_Medium')}>
													View similar item
												</LencoTypography>
											</LencoButton>
										)}
									</Box>
								</Box>
							}
						/>
					</Box>
				);
			})}
		</Box>
	);
};

export default CartItemList;
